import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    BookOpen, ClipboardCheck, BarChart3, ArrowRight, Loader2,
    AlertTriangle, X, ChevronLeft, ChevronDown, FileText, Download, Sliders,
    Calendar, Upload, ChevronRight, LogOut, Clock, Target, Printer, RotateCcw, Save, FolderOpen, GraduationCap, Check, Monitor, Settings, User, Trash2, Flag,
    ShoppingCart, ExternalLink, Hourglass, Shield,
    Sun, Moon, Sparkles, MessageCircle,
    TrendingUp, Users, Bell, Activity, Zap
} from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import api, { devCenter, adminAnalytics, operatingState, profileDocuments, teacherContext } from '../services/api';
import { downloadFile } from '../utils/download';
import { TeacherControlPanel } from './ClassroomBoard';
import GradingStudio from './GradingStudio';
import PostGenerationView from './PostGenerationView';
import useProgressMessages from '../hooks/useProgressMessages';
import CampusIntelligenceHub from './CampusIntelligenceHub';
import TeacherOperatingCenter from './TeacherOperatingCenter';
import { teacherDashboardMockData, teacherDashboardMockEvents } from '../data/teacherDashboardMockData';
import TeacherClassesWorkspace from './workspace/TeacherClassesWorkspace';
import TeacherResourcesWorkspace from './workspace/TeacherResourcesWorkspace';
import TeacherWorkspaceHeader from './workspace/TeacherWorkspaceHeader';
import TeacherWorkspaceOverlays from './workspace/TeacherWorkspaceOverlays';
import UnavailableWorkspace from './workspace/UnavailableWorkspace';
import { useTheme } from '../contexts/ThemeContext';
import { trackEvent, trackFirstLesson } from '../utils/telemetry';
import { useFeatureArea, useToolTracking } from '../hooks/useTelemetry';
import { buildReadinessItems, getReadinessSummary } from '../utils/lessonReliability';
import { buildTeacherOperatingState } from '../utils/teacherOperatingState';
import {
    getUserRoleVariant,
    shouldRenderAdminWorkspace,
    shouldRenderTeacherWorkspace,
} from '../utils/roleUtils';


/* ── Feature Flags ── */

/* ── Objective validator: safe fallback for malformed objectives (no heuristic rewriting) ── */
const validateObjective = (text) => {
    if (!text || text.trim().length < 10) {
        console.warn('[Saige] Malformed objective detected:', JSON.stringify(text));
        return "I can demonstrate understanding of today's objective.";
    }
    // Detect: "I can" followed by adjective/noun (missing verb)
    const iCanBadPattern = /^I can\s+(basic|key|the|my|our|simple|complex|advanced|important)\b/i;
    if (iCanBadPattern.test(text)) {
        console.warn('[Saige] Malformed objective detected (missing verb):', JSON.stringify(text));
        return "I can demonstrate understanding of today's objective.";
    }
    return text;
};

/* ── Time-of-day greeting ── */
const getTeacherFirstName = (name) => {
    const first = name?.trim()?.split(/\s+/)[0];
    return first || 'Teacher';
};

const getGreeting = (name) => {
    const hour = new Date().getHours();
    const first = getTeacherFirstName(name);
    if (hour < 12) return `Good morning, ${first}.`;
    if (hour < 17) return `Good afternoon, ${first}.`;
    return `Good evening, ${first}.`;
};

const TEACHER_SIDEBAR_ITEMS = [
    { id: 'home', label: 'Home', icon: Activity },
    { id: 'classes', label: 'Classes', icon: BookOpen },
    { id: 'resources', label: 'Resources', icon: FolderOpen },
    { id: 'assistant', label: 'Assistant', icon: Sparkles },
    { id: 'settings', label: 'Settings', icon: Settings, action: 'settings' },
];

const getTeacherHomePrimaryCourse = (operatingState = {}) => {
    const courses = operatingState.courses || [];
    const primaryCourseId = operatingState.operatingBrief?.primaryCourseId;
    const explicitCourse = courses.find((course) => course.id === primaryCourseId);

    return explicitCourse || courses[0] || null;
};

/* ── Date-aware prompt for lesson planning heading ── */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getDatePrompt = (dateStr, numDays = 1) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

    if (numDays > 1) {
        const dayLabel = diffDays === 0 ? 'today'
            : diffDays === 1 ? 'tomorrow'
            : diffDays <= 5 ? `this ${DAY_NAMES[target.getDay()]}`
            : `${MONTH_NAMES[target.getMonth()]} ${target.getDate()}`;
        return `Planning ${numDays} days starting ${dayLabel}`;
    }

    if (diffDays < 0) {
        return `Planning a lesson for ${MONTH_NAMES[target.getMonth()]} ${target.getDate()}.`;
    }
    if (diffDays === 0) return 'What are you teaching today?';
    if (diffDays === 1) return 'What are you teaching tomorrow?';
    if (diffDays <= 5) return `What are you teaching this ${DAY_NAMES[target.getDay()]}?`;
    return `What are you teaching on ${MONTH_NAMES[target.getMonth()]} ${target.getDate()}?`;
};

/* ══════════════════════════════════════════════════════════════
   ROLE-AWARE WORKSPACE PRIORITY PANELS
   Each role gets a focused priority surface at the top of their workspace.
══════════════════════════════════════════════════════════════ */

/* ── Admin Priority Workspace ── */
const AdminPriorityWorkspace = ({ onEnterAdminWorkspace, onOpenAssistant }) => {
    const [watchItems, setWatchItems] = useState([]);
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [watchRes, usageRes] = await Promise.allSettled([
                    adminAnalytics.getWatchItems?.() || Promise.resolve({ data: { items: [] } }),
                    adminAnalytics.getWorkspaceSummary?.() || Promise.resolve({ data: null }),
                ]);
                setWatchItems(watchRes.status === 'fulfilled' ? (watchRes.value?.data?.items?.slice(0, 3) || []) : []);
                setUsageData(usageRes.status === 'fulfilled' ? usageRes.value?.data : null);
            } catch (e) {
                console.warn('[AdminWorkspace] load error:', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
        >
            {/* Enter Admin Workspace — primary CTA */}
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-6 text-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-[0_4px_12px_rgba(255,193,7,0.4)]">
                        <BarChart3 size={18} className="text-black" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-white">Admin Workspace</h3>
                        <p className="text-xs text-slate-400">Analytics · campus intelligence · staff activity</p>
                    </div>
                </div>
                {!loading && usageData && (
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <div className="bg-white/5 rounded-xl p-3 border border-white/8">
                            <div className="text-2xl font-bold text-white">{usageData.active_users_7d ?? '—'}</div>
                            <div className="text-xs text-slate-400 mt-0.5">Active staff (7d)</div>
                        </div>
                        <div className="bg-white/5 rounded-xl p-3 border border-white/8">
                            <div className="text-2xl font-bold text-white">{usageData.lessons_generated_7d ?? '—'}</div>
                            <div className="text-xs text-slate-400 mt-0.5">Lessons generated (7d)</div>
                        </div>
                    </div>
                )}
                {loading && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-5">
                        <Loader2 size={14} className="animate-spin" />
                        Loading campus activity…
                    </div>
                )}
                <button
                    onClick={onEnterAdminWorkspace}
                    className="w-full py-3 bg-[#FFC107] text-black rounded-xl font-semibold text-sm hover:bg-[#e6ad00] transition-colors flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(255,193,7,0.3)]"
                >
                    <TrendingUp size={16} />
                    Open Admin Workspace
                    <ArrowRight size={15} />
                </button>
            </div>

            {/* Watch Items */}
            {!loading && watchItems.length > 0 && (
                <div className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-5 py-4 border-b border-black/5 flex items-center gap-2">
                        <Bell size={15} className="text-rose-500" />
                        <h3 className="text-sm font-semibold text-[#1d1d1f]">Watch Items</h3>
                        <span className="ml-auto text-xs font-semibold bg-rose-50 text-rose-500 px-2 py-0.5 rounded-full">
                            {watchItems.length}
                        </span>
                    </div>
                    <div className="divide-y divide-black/5">
                        {watchItems.map((item, idx) => (
                            <div key={idx} className="px-5 py-3 flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    item.severity === 'critical' ? 'bg-red-500'
                                    : item.severity === 'warning' ? 'bg-amber-400'
                                    : 'bg-slate-300'
                                }`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[#1d1d1f] font-medium truncate">{item.title || item.message}</p>
                                    {item.detail && <p className="text-xs text-[#999] truncate">{item.detail}</p>}
                                </div>
                                <ArrowRight size={13} className="text-[#bbb] flex-shrink-0" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick assist */}
            <button
                onClick={onOpenAssistant}
                className="w-full py-3 flex items-center justify-center gap-2 text-sm font-medium text-[#444] bg-white border border-black/5 rounded-2xl hover:bg-[#fafafa] hover:border-amber-200 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            >
                <Sparkles size={15} className="text-amber-500" />
                Ask Saige something about your campus
            </button>
        </motion.div>
    );
};

/* ── Counselor Priority Workspace ── */
// eslint-disable-next-line no-unused-vars
const CounselorPriorityWorkspace = ({ onOpenAssistant, onOpenSettings }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-4"
    >
        {/* Role banner */}
        <div className="bg-gradient-to-br from-[#0C4A6E] to-[#075985] rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-300 to-cyan-400 flex items-center justify-center">
                    <Users size={18} className="text-[#0C4A6E]" />
                </div>
                <div>
                    <h3 className="text-base font-bold">Counselor Workspace</h3>
                    <p className="text-xs text-sky-300">Student support · campus procedures · communications</p>
                </div>
            </div>
            <p className="text-sm text-sky-100 leading-relaxed">
                Welcome. Use Saige to draft parent communications, navigate referral procedures,
                and stay on top of upcoming campus dates.
            </p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
            {[
                { icon: '💬', label: 'Draft parent communication', role: 'counselor' },
                { icon: '📋', label: 'Referral procedures', role: 'counselor' },
                { icon: '🗓️', label: 'Upcoming dates', role: 'counselor' },
                { icon: '🆘', label: 'Student support resources', role: 'counselor' },
            ].map((item, idx) => (
                <button
                    key={idx}
                    onClick={onOpenAssistant}
                    className="p-4 bg-white rounded-2xl border border-black/5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] text-left hover:border-sky-200 hover:shadow-md transition-all"
                >
                    <div className="text-xl mb-2">{item.icon}</div>
                    <p className="text-xs font-medium text-[#1d1d1f] leading-tight">{item.label}</p>
                </button>
            ))}
        </div>

        <button
            onClick={onOpenAssistant}
            className="w-full py-3 flex items-center justify-center gap-2 text-sm font-medium text-[#444] bg-white border border-black/5 rounded-2xl hover:bg-[#fafafa] hover:border-sky-200 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
        >
            <Sparkles size={15} className="text-sky-500" />
            Ask Saige for counseling support
        </button>
    </motion.div>
);

/* ── Support Staff Priority Workspace ── */
const SupportStaffPriorityWorkspace = ({ onOpenAssistant }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-4"
    >
        {/* Role banner */}
        <div className="bg-gradient-to-br from-[#14532D] to-[#166534] rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-300 to-green-400 flex items-center justify-center">
                    <Activity size={18} className="text-[#14532D]" />
                </div>
                <div>
                    <h3 className="text-base font-bold">Campus Support Workspace</h3>
                    <p className="text-xs text-emerald-300">Procedures · contacts · campus calendar</p>
                </div>
            </div>
            <p className="text-sm text-emerald-100 leading-relaxed">
                Find campus procedures, locate the right contacts, and get help drafting
                communications — all in one place.
            </p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
            {[
                { icon: '📋', label: 'Campus procedures today' },
                { icon: '📞', label: 'Who do I contact?' },
                { icon: '🗓️', label: "This week's calendar" },
                { icon: '📣', label: 'Draft campus message' },
            ].map((item, idx) => (
                <button
                    key={idx}
                    onClick={onOpenAssistant}
                    className="p-4 bg-white rounded-2xl border border-black/5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] text-left hover:border-emerald-200 hover:shadow-md transition-all"
                >
                    <div className="text-xl mb-2">{item.icon}</div>
                    <p className="text-xs font-medium text-[#1d1d1f] leading-tight">{item.label}</p>
                </button>
            ))}
        </div>

        <button
            onClick={onOpenAssistant}
            className="w-full py-3 flex items-center justify-center gap-2 text-sm font-medium text-[#444] bg-white border border-black/5 rounded-2xl hover:bg-[#fafafa] hover:border-emerald-200 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
        >
            <Sparkles size={15} className="text-emerald-500" />
            Ask Saige for campus support
        </button>
    </motion.div>
);

const ProductionDashboard = ({ userProfile, onLogout, onEnterAdminWorkspace, onEnterDevCenter }) => {
    const { isDark, toggleTheme } = useTheme();

    // ── Role detection — derived from localStorage + profile ──
    const userRole = getUserRoleVariant(userProfile);
    const showAdminWorkspace = shouldRenderAdminWorkspace(userRole);

    const [activePipeline, setActivePipeline] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showAssistantDrawer, setShowAssistantDrawer] = useState(false);
    const [activeTeacherSidebarTab, setActiveTeacherSidebarTab] = useState('home');



    // Subject/Grade from profile
    const [subjects, setSubjects] = useState(userProfile?.subjects || []);
    const [gradeLevel, setGradeLevel] = useState(userProfile?.grade_level || '');
    const [selectedSubject, setSelectedSubject] = useState(userProfile?.subjects?.[0] || '');

    // Onboarding state for incomplete profiles
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(1); // 1 = grade/subjects, 2 = curriculum upload
    const [onboardingGrade, setOnboardingGrade] = useState('');
    const [onboardingSubjects, setOnboardingSubjects] = useState([]);
    const [savingOnboarding, setSavingOnboarding] = useState(false);

    // Curriculum upload state
    const [curriculumFile, setCurriculumFile] = useState(null);
    const [uploadingCurriculum, setUploadingCurriculum] = useState(false);
    const [, setCurriculumUploaded] = useState(false);
    const curriculumInputRef = useRef(null);

    // Lesson Planning State
    const [lessonDate, setLessonDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        // Skip weekends
        while (tomorrow.getDay() === 0 || tomorrow.getDay() === 6) {
            tomorrow.setDate(tomorrow.getDate() + 1);
        }
        return tomorrow.toISOString().split('T')[0];
    });
    const [numDays, setNumDays] = useState(1);
    const [differentiationFocus, setDifferentiationFocus] = useState('');
    const [bellRingerType, _setBellRingerType] = useState('auto'); // Always-on: backend generates bell ringer when type is 'auto'
    const [lessonPlans, setLessonPlans] = useState([]); // For multi-day results

    // v2 Simplified form state
    const [showMoreOptions, setShowMoreOptions] = useState(false);



    // Calendar State
    const [calendarMonth, _setCalendarMonth] = useState(new Date());
    const [calendarEvents, setCalendarEvents] = useState({});
    const [calendarLoading, setCalendarLoading] = useState(false); // eslint-disable-line no-unused-vars
    const [selectedDay, setSelectedDay] = useState(null); // For day detail modal
    const [dayDocuments, setDayDocuments] = useState([]); // Saved lessons for selected day
    const [dayDocsLoading, setDayDocsLoading] = useState(false);
    const [expandedDayDoc, setExpandedDayDoc] = useState(null); // Expanded doc id
    const [, setStandardsProgress] = useState(null); // TEKS tracker
    const [reteachFlags, setReteachFlags] = useState({}); // Reteach flags

    // My Documents State
    const [savedDocuments, setSavedDocuments] = useState([]);
    const [profileContextDocuments, setProfileContextDocuments] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [resourcesError, setResourcesError] = useState(null);
    const [showMyDocuments, setShowMyDocuments] = useState(false);
    const [savingDocument, setSavingDocument] = useState(false);
    const [showSavedLessons, setShowSavedLessons] = useState(false);

    // Post-Generation Action Menu State
    const [generatingSlides, setGeneratingSlides] = useState(false);
    const [generatingPacket, setGeneratingPacket] = useState(false);
    const [generatingQuiz, setGeneratingQuiz] = useState(false);
    const [generatingDayLabel, setGeneratingDayLabel] = useState('');
    const [actionError, setActionError] = useState(null);

    // Quiz Modal State
    const [quizData, setQuizData] = useState(null);
    const [showQuizModal, setShowQuizModal] = useState(false);

    // Toast notification state
    const [toast, setToast] = useState({ message: '', type: 'success', visible: false });
    const showToast = (message, type = 'success') => {
        setToast({ message, type, visible: true });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3500);
    };

    // Classroom Board State
    const [showClassroomBoard, setShowClassroomBoard] = useState(false);
    const [lessonData, setLessonData] = useState(null);  // Structured lesson content for board
    const [lessonId, setLessonId] = useState(null);
    // Phase 2.1: explicit board mode — 'edit' | 'presenter' | 'display'
    // Starts as 'edit' every time the board opens. Teacher clicks 'Go Live' to enter 'display'.
    const [boardMode, setBoardMode] = useState('edit');

    // Grading Studio State
    const [showGradingStudio, setShowGradingStudio] = useState(false);

    // v2 Sprint 2 — Post-Generation View (canonical post-generation workspace)
    const [showPostGenerationView, setShowPostGenerationView] = useState(false);
    const [lastGeneratedResult, setLastGeneratedResult] = useState(null);
    // Context passed from Plan Tomorrow → next generation cycle
    const [nextDayContext, setNextDayContext] = useState(null); // { previous_day_activities: string[] }
    const [autoGenerate, setAutoGenerate] = useState(false); // triggers auto-submit after Plan Tomorrow

    // Account Settings Modal State
    const [showAccountSettings, setShowAccountSettings] = useState(false);

    // Admin Dashboard State
    // Initialize from localStorage role (set at login); /me overrides authoritatively
    const [isAdmin, setIsAdmin] = useState(() => {
        const role = localStorage.getItem('saige_role');
        return role === 'district_admin' || role === 'campus_admin';
    });
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);

    // Dev Command Center access — checked via canonical backend endpoint
    const [hasDevCenterAccess, setHasDevCenterAccess] = useState(false);
    const [showAdminMenu, setShowAdminMenu] = useState(false);

    // Verify admin status from backend — overrides localStorage if reachable
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const response = await api.get('/me');
                setIsAdmin(response.data.is_admin === true);
            } catch {
                // /me unreachable — keep localStorage-derived value (fail-open for display)
            }
        };
        checkAdmin();
    }, [userProfile.email]);

    // Check Dev Center access separately — backend is source of truth
    useEffect(() => {
        const checkDevCenter = async () => {
            try {
                const res = await devCenter.getAccess();
                setHasDevCenterAccess(res.data?.authorized === true);
            } catch {
                setHasDevCenterAccess(false);
            }
        };
        checkDevCenter();
    }, [userProfile.email]);

    // Logout confirmation state
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // Rotating progress messages for each pipeline
    const lessonProgressMsg = useProgressMessages(isLoading && activePipeline === 'lesson', [
        'Building your lesson...',
        'Aligning objectives...',
        'Selecting engagement strategies...',
        'Structuring activities...',
        'Adding differentiation...',
        'Polishing your lesson plan...'
    ]);


    const [lessonTopics, setLessonTopics] = useState('');
    const [lessonReadiness, setLessonReadiness] = useState(null);
    const [lessonReadinessLoading, setLessonReadinessLoading] = useState(false);
    const [backendOperatingState, setBackendOperatingState] = useState(null);




    // Sync grade/subject from DB (source of truth for AccountSettings changes)
    useEffect(() => {
        const syncProfile = async () => {
            if (!userProfile?.email) return;
            try {
                const response = await api.get('/get-school-profile');
                if (response.data?.profile?.grade_level) {
                    setGradeLevel(response.data.profile.grade_level);
                }
                if (response.data?.profile?.subjects?.length) {
                    setSubjects(response.data.profile.subjects);
                    setSelectedSubject(prev => response.data.profile.subjects.includes(prev) ? prev : response.data.profile.subjects[0]);
                }
            } catch {
                console.log('Could not sync profile');
            }
        };
        syncProfile();
    }, [userProfile?.email]);

    useEffect(() => {
        if (!userProfile?.email) {
            setBackendOperatingState(null);
            return;
        }

        let cancelled = false;

        const loadOperatingState = async () => {
            try {
                const response = await operatingState.getMine();
                if (!cancelled) {
                    setBackendOperatingState(response.data || null);
                }
            } catch (err) {
                console.warn('[TeacherOperatingState] backend read model unavailable:', err?.message || err);
                if (!cancelled) {
                    setBackendOperatingState(null);
                }
            }
        };

        loadOperatingState();

        return () => {
            cancelled = true;
        };
    }, [userProfile?.email, userProfile?.district_id, userProfile?.campus_id]);

    useEffect(() => {
        if (activePipeline !== 'lesson' || !userProfile?.email) {
            setLessonReadiness(null);
            setLessonReadinessLoading(false);
            return;
        }

        let cancelled = false;

        const loadLessonReadiness = async () => {
            setLessonReadinessLoading(true);
            try {
                const response = await api.post('/api/production/lesson-readiness', {
                    teacher_id: userProfile.email,
                    subject: selectedSubject || null,
                });
                if (!cancelled) {
                    setLessonReadiness(response.data || null);
                }
            } catch (err) {
                console.error('Lesson readiness check failed:', err);
                if (!cancelled) setLessonReadiness(null);
            } finally {
                if (!cancelled) setLessonReadinessLoading(false);
            }
        };

        loadLessonReadiness();

        return () => {
            cancelled = true;
        };
    }, [activePipeline, selectedSubject, userProfile?.email]);



    // Fetch calendar events when month changes
    useEffect(() => {
        const fetchCalendarEvents = async () => {
            setCalendarLoading(true);
            try {
                const response = await api.post('/api/production/calendar-events', {
                    teacher_id: userProfile?.email,
                    year: calendarMonth.getFullYear(),
                    month: calendarMonth.getMonth() + 1
                });
                if (response.data?.events) {
                    setCalendarEvents(prev => ({ ...prev, ...response.data.events }));
                }
                if (response.data?.standards_progress) {
                    setStandardsProgress(response.data.standards_progress);
                }
            } catch (err) {
                console.error('Failed to fetch calendar events:', err);
            } finally {
                setCalendarLoading(false);
            }
        };
        fetchCalendarEvents();
    }, [calendarMonth, userProfile?.email]);

    // Fetch reteach flags on mount and when month changes
    useEffect(() => {
        const fetchReteachFlags = async () => {
            if (!userProfile?.email) return;
            try {
                const response = await api.get('/api/production/reteach-flags', {
                    params: { teacher_id: userProfile.email }
                });
                if (response.data?.flags) {
                    setReteachFlags(response.data.flags);
                }
            } catch (err) {
                console.error('Failed to fetch reteach flags:', err);
            }
        };
        fetchReteachFlags();
    }, [calendarMonth, userProfile?.email]);

    // Fetch saved lessons for selected calendar day
    useEffect(() => {
        if (!selectedDay?.dateStr || !userProfile?.email) {
            setDayDocuments([]);
            setExpandedDayDoc(null);
            return;
        }
        const fetchDayDocs = async () => {
            setDayDocsLoading(true);
            try {
                const response = await api.get('/api/documents/list', {
                    params: { teacher_id: userProfile.email }
                });
                const allDocs = response.data.documents || [];
                // Filter by date in metadata
                const filtered = allDocs.filter(doc =>
                    doc.metadata?.date === selectedDay.dateStr ||
                    doc.metadata?.lesson_date === selectedDay.dateStr
                );
                setDayDocuments(filtered);
            } catch (err) {
                console.error('Failed to fetch day documents:', err);
                setDayDocuments([]);
            } finally {
                setDayDocsLoading(false);
            }
        };
        fetchDayDocs();
    }, [selectedDay?.dateStr, userProfile?.email]);

    // NOTE: Grade/subject selection is now collected during signup via OnboardingWizard.jsx
    // This redundant onboarding modal has been disabled to avoid asking users twice.
    // The useEffect below that would show the modal is intentionally commented out.
    // If users need to update their grade/subject later, they can do so via settings.
    /*
    useEffect(() => {
        const onboardingComplete = localStorage.getItem('saige_onboarding_complete');
        if (onboardingComplete === 'true') {
            setShowOnboarding(false);
            return;
        }

        const hasGrade = userProfile?.grade_level && userProfile.grade_level.trim() !== '';
        const hasSubjects = userProfile?.subjects && userProfile.subjects.length > 0;

        if (!hasGrade || !hasSubjects) {
            setShowOnboarding(true);
        } else {
            // Profile complete from DB, mark as done
            localStorage.setItem('saige_onboarding_complete', 'true');
        }
    }, [userProfile]);
    */

    // Handle onboarding Step 1 submission (grade/subjects)
    const handleOnboardingStep1 = async () => {
        if (!onboardingGrade || onboardingSubjects.length === 0) {
            showToast('Please select your grade level and at least one subject', 'error');
            return;
        }

        setSavingOnboarding(true);
        try {
            await api.post('/api/update-user', {
                grade_level: onboardingGrade,
                subjects: onboardingSubjects
            });

            // Update local state
            setGradeLevel(onboardingGrade);
            setSubjects(onboardingSubjects);
            setSelectedSubject(onboardingSubjects[0]);

            // Move to step 2 (curriculum upload)
            setOnboardingStep(2);
        } catch (err) {
            console.error('Error saving profile:', err);
            showToast('Failed to save profile. Please try again.', 'error');
        } finally {
            setSavingOnboarding(false);
        }
    };

    // Handle curriculum file upload
    const handleCurriculumUpload = async () => {
        if (!curriculumFile) {
            // Skip - just finish onboarding
            finishOnboarding();
            return;
        }

        setUploadingCurriculum(true);
        try {
            // Primary: extract structured curriculum_data into school_profile
            const formData = new FormData();
            formData.append('file', curriculumFile);
            formData.append('subject', onboardingSubjects[0] || 'General');

            await api.post('/api/upload-curriculum', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setCurriculumUploaded(true);

            // Secondary (non-blocking): also store in visible profile doc list
            // so it appears in /get-profile-docs. Failure here is non-fatal.
            profileDocuments.upload(curriculumFile).catch(err => {
                console.warn('Visible doc listing failed (non-fatal):', err);
            });

            finishOnboarding();
        } catch (err) {
            console.error('Error uploading curriculum:', err);
            showToast('Failed to upload curriculum. You can add it later from settings.', 'error');
            finishOnboarding();
        } finally {
            setUploadingCurriculum(false);
        }
    };

    // Finish onboarding and close modal
    const finishOnboarding = () => {
        localStorage.setItem('saige_onboarding_complete', 'true');
        setShowOnboarding(false);
        setOnboardingStep(1);
    };

    // Toggle subject selection in onboarding
    const toggleOnboardingSubject = (subject) => {
        setOnboardingSubjects(prev =>
            prev.includes(subject)
                ? prev.filter(s => s !== subject)
                : [...prev, subject]
        );
    };

    const teacherFirstName = getTeacherFirstName(userProfile?.name);
    const greetingText = getGreeting(userProfile?.name);
    const teacherOperatingProfile = useMemo(() => ({
        ...(userProfile || {}),
        subjects: subjects.length > 0 ? subjects : userProfile?.subjects,
        gradeLevel: gradeLevel || userProfile?.gradeLevel,
        grade_level: gradeLevel || userProfile?.grade_level,
    }), [userProfile, subjects, gradeLevel]);
    const teacherOperatingState = useMemo(() => buildTeacherOperatingState({
        userProfile: teacherOperatingProfile,
        calendarEvents,
        lessonReadiness,
        savedDocuments,
        fallbackData: teacherDashboardMockData,
        fallbackCalendarEvents: teacherDashboardMockEvents,
        backendOperatingState,
    }), [teacherOperatingProfile, calendarEvents, lessonReadiness, savedDocuments, backendOperatingState]);
    const teacherHomePrimaryCourse = getTeacherHomePrimaryCourse(teacherOperatingState);
    const teacherHomeReadyLine = `${teacherHomePrimaryCourse?.name || 'Engineering'} is ready for tomorrow.`;
    const showTeacherWorkspace = shouldRenderTeacherWorkspace({
        userRole,
        subjects,
        gradeLevel,
        userProfile,
    });

    const handleGenerateCourseLesson = (course) => {
        if (course?.name) {
            setSelectedSubject(course.name);
        }
        setActivePipeline('lesson');
    };

    const openAssistantDrawer = () => {
        setShowAssistantDrawer(true);
        setActivePipeline(null);
        setResult(null);
        setError(null);
        setLessonPlans([]);
    };

    const handleTeacherSidebarClick = (item) => {
        if (item.disabled) return;

        if (item.action === 'settings') {
            setShowAccountSettings(true);
            return;
        }

        if (item.id === 'assistant') {
            openAssistantDrawer();
            return;
        }

        setActivePipeline(null);
        setResult(null);
        setError(null);
        setLessonPlans([]);
        setShowAssistantDrawer(false);
        setActiveTeacherSidebarTab(item.id);
    };

    // Save lesson plans to My Documents
    const saveAllLessonPlans = async () => {
        if (lessonPlans.length === 0) return;

        setSavingDocument(true);
        try {
            for (const plan of lessonPlans) {
                const dateFormatted = new Date(plan.date).toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric'
                });
                await api.post('/api/documents/save', {
                    teacher_id: userProfile?.email,
                    doc_type: 'lesson',
                    title: plan.topic
                        ? `${selectedSubject} - ${dateFormatted} - ${plan.topic}`
                        : `${selectedSubject} - ${dateFormatted}`,
                    content: plan.text || '',
                    metadata: {
                        date: plan.date,
                        subject: selectedSubject,
                        differentiation: differentiationFocus,
                        lesson_id: plan.lesson_id || plan.data?.lesson_id
                    }
                });
            }
            showToast('Lesson plans saved to My Documents!', 'success');
        } catch (err) {
            console.error('Error saving lesson plans:', err);
            showToast('Failed to save lesson plans', 'error');
        } finally {
            setSavingDocument(false);
        }
    };

    // Fetch saved documents
    const fetchMyDocuments = async () => {
        try {
            const response = await api.get('/api/documents/list', {
                params: { teacher_id: userProfile?.email }
            });
            setSavedDocuments(response.data.documents || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
        }
    };

    const fetchResourcesWorkspaceData = async () => {
        setResourcesLoading(true);
        setResourcesError(null);

        const [savedDocsResult, profileContextDocsResult, courseContextDocsResult] = await Promise.allSettled([
            api.get('/api/documents/list', {
                params: { teacher_id: userProfile?.email }
            }),
            profileDocuments.list(),
            teacherContext.list(),
        ]);

        if (savedDocsResult.status === 'fulfilled') {
            setSavedDocuments(savedDocsResult.value.data.documents || []);
        }

        const profileDocs = profileContextDocsResult.status === 'fulfilled'
            ? profileContextDocsResult.value.data?.documents || []
            : [];
        const courseContextDocs = courseContextDocsResult.status === 'fulfilled'
            ? (courseContextDocsResult.value.data?.documents || []).map((doc) => ({
                ...doc,
                id: `course-context-${doc.id}`,
                name: doc.title || doc.filename || 'Course context',
                type: doc.doc_type || 'course_context',
            }))
            : [];

        if (profileContextDocsResult.status === 'fulfilled' || courseContextDocsResult.status === 'fulfilled') {
            setProfileContextDocuments([...profileDocs, ...courseContextDocs]);
        }

        if (
            savedDocsResult.status === 'rejected' &&
            profileContextDocsResult.status === 'rejected' &&
            courseContextDocsResult.status === 'rejected'
        ) {
            setResourcesError('Resources could not be loaded. Existing saved entry points are still available.');
        }

        setResourcesLoading(false);
    };

    const handleCloseAccountSettings = () => {
        setShowAccountSettings(false);
        if (activeTeacherSidebarTab === 'resources') {
            fetchResourcesWorkspaceData();
        }
    };

    // Load documents when showing My Documents
    useEffect(() => {
        if (showMyDocuments) {
            fetchMyDocuments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMyDocuments]);

    useEffect(() => {
        if (activeTeacherSidebarTab === 'resources') {
            fetchResourcesWorkspaceData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTeacherSidebarTab, userProfile?.email]);

    // v2 Sprint 2 — Auto-generate after Plan Tomorrow sets the next date
    useEffect(() => {
        if (autoGenerate && activePipeline === 'lesson') {
            setAutoGenerate(false);
            handleGenerateLesson();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoGenerate, activePipeline]);

    // Feature area: teacher is using lesson planner when pipeline is active
    useFeatureArea(activePipeline === 'lesson' ? 'lesson_planner' : null);

    // Tool tracking: classroom board
    useToolTracking('classroom_board', showClassroomBoard);

    // Reliability hardening: map backend failure_code to teacher-safe messages
    const [showTopicHint, setShowTopicHint] = useState(false);

    const _getTeacherFriendlyError = (failureCode, fallbackMsg) => {
        const messages = {
            PROFILE_INCOMPLETE: 'Please complete your grade and subject in Settings before generating.',
            GENERATION_FAILED_AFTER_RETRIES: 'We could not produce a usable lesson after retries. Please try again.',
            MODEL_TRANSIENT_FAILURE: 'Our AI system is temporarily busy. Please try again in a moment.',
            GENERIC_FALLBACK_USED: 'Saige used a safe generic fallback lesson.',
            GENERIC_FALLBACK_REJECTED: 'Saige couldn\'t produce a specific lesson for this date. Please try again or adjust your topic.',
            SCOPE_RESOLUTION_FAILED: 'Unable to determine lesson scope. Please try a different date.',
            MODEL_EXHAUSTED_RETRIES: 'Our AI system is temporarily busy. Please try again in a moment.',
            QUALITY_GATE_FAILED: 'The generated lesson did not meet quality standards. Please try again.',
            UNKNOWN_GENERATION_FAILURE: 'Something went wrong during generation. Please try again.',
        };
        // Surface topic hint for fallback rejection
        if (failureCode === 'GENERIC_FALLBACK_REJECTED') {
            setShowTopicHint(true);
        }
        return messages[failureCode] || fallbackMsg || 'Generation failed. Please try again.';
    };

    // Pipeline handlers
    const handleGenerateLesson = async () => {
        setIsLoading(true);
        setError(null);
        setShowTopicHint(false);
        setLessonPlans([]);
        setGeneratingDayLabel('');

        // Emit generation_started
        trackEvent('generation_started', {
            generation_type: 'lesson',
            subject: selectedSubject,
            num_days: numDays,
        });

        try {
            const plans = [];
            const topicForThisBatch = lessonTopics?.trim() || ''; // Capture before clearing
            let completedDays = 0;

            // Parse date as local date to avoid timezone issues
            // new Date("YYYY-MM-DD") creates UTC midnight, which can shift days in local time
            const [year, month, day] = lessonDate.split('-').map(Number);
            let currentDate = new Date(year, month - 1, day); // month is 0-indexed

            // Ensure start date isn't a weekend
            while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
                currentDate.setDate(currentDate.getDate() + 1);
            }

            for (let i = 0; i < numDays; i++) {
                // If not the first day, move to next day and check weekend
                if (i > 0) {
                    currentDate.setDate(currentDate.getDate() + 1);
                    while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                }

                // Show day progress for multi-day
                if (numDays > 1) {
                    setGeneratingDayLabel(`Generating Day ${i + 1} of ${numDays}...`);
                }

                // Format date as YYYY-MM-DD using local date components (avoids toISOString UTC shift)
                const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

                // Extract previous day's activity names for differentiation
                // Use nextDayContext if set by Plan Tomorrow, otherwise use multi-day chain
                let previousDayActivities = nextDayContext?.previous_day_activities || null;
                if (!previousDayActivities && i > 0 && plans[i - 1]?.data?.lesson_flow) {
                    previousDayActivities = plans[i - 1].data.lesson_flow
                        .map(item => item.activity)
                        .filter(Boolean);
                }
                // Clear nextDayContext after consuming it
                if (nextDayContext) setNextDayContext(null);

                try {
                    const response = await api.post('/api/production/generate-lesson', {
                        teacher_id: userProfile.email,
                        lesson_date: dateStr,
                        subject: selectedSubject,
                        topic: lessonTopics || null,
                        differentiation_focus: differentiationFocus || null,
                        bell_ringer_type: bellRingerType || null,
                        day_index: numDays > 1 ? i + 1 : null,
                        total_days: numDays > 1 ? numDays : null,
                        previous_day_activities: previousDayActivities,
                    });

                    // Check if generation failed (backend returns success=false with failure_code)
                    if (response.data.success === false || !response.data.data) {
                        const err = new Error(response.data.error || 'Lesson generation failed');
                        err.failure_code = response.data.failure_code;
                        err.request_id = response.data.request_id;
                        throw err;
                    }

                    plans.push({
                        date: dateStr,
                        topic: topicForThisBatch || null,
                        ...response.data
                    });
                    completedDays++;
                } catch (dayErr) {
                    // Profile incomplete — surface a clear action path
                    const errDetail = dayErr.response?.data?.detail;
                    const backendFailureCode = dayErr.failure_code || dayErr.response?.data?.failure_code;
                    if (dayErr.response?.status === 422 && errDetail?.error === 'profile_incomplete') {
                        setError('profile_incomplete');
                        return;
                    }
                    if (backendFailureCode === 'PROFILE_INCOMPLETE') {
                        setError('profile_incomplete');
                        return;
                    }
                    // Use backend request_id as sole incident reference
                    const backendRef = dayErr.request_id
                        || dayErr.response?.data?.request_id
                        || 'unknown';
                    const failedStep = _getTeacherFriendlyError(backendFailureCode, dayErr.message);
                    const dayLabel = `Day ${i + 1}`;

                    if (completedDays > 0) {
                        setLessonPlans(plans);
                        setError(
                            `Failed at ${dayLabel}: ${failedStep}. ` +
                            `Days 1-${completedDays} saved successfully. Ref: ${backendRef}`
                        );
                    } else {
                        setError(
                            `Failed at ${dayLabel}: ${failedStep}. Ref: ${backendRef}`
                        );
                    }
                    return; // Exit loop — partial results already saved
                }
            }

            setLessonPlans(plans);
            setLessonTopics(''); // Clear topics after successful generation
            if (plans.length === 1) {
                // Only open modal if we have valid lesson data
                if (plans[0].data && plans[0].success !== false) {
                    setResult({ ...plans[0], success: true });
                    // Store structured data for ClassroomBoard
                    if (plans[0].data) {
                        setLessonData(plans[0].data);
                    }
                    if (plans[0].lesson_id) {
                        setLessonId(plans[0].lesson_id);
                    }
                    // v2: Open PostGenerationView instead of modal
                    setLastGeneratedResult({ ...plans[0], success: true });
                    setShowPostGenerationView(true);
                    setActivePipeline(null);
                    // Sprint 3.5 telemetry
                    trackFirstLesson();
                    trackEvent('lesson_generated', {
                        template_source: plans[0].data?.template_info?.template_source || 'unknown',
                        has_uploaded_curriculum: !!plans[0].data?.curriculum_content,
                        generation_mode: plans[0].data?.generation_mode || null,
                        failure_types: plans[0].data?.generation_receipt?.failure_types || [],
                        fallback_used: !!plans[0].data?.fallback_used,
                        warning_count: plans[0].data?.generation_receipt?.warnings?.length || 0,
                        template_status: plans[0].data?.generation_receipt?.template?.status || null,
                        context_pack_status: plans[0].data?.generation_receipt?.context_pack?.status || null,
                    });
                    trackEvent('generation_completed', {
                        generation_type: 'lesson',
                        subject: selectedSubject,
                        num_days: numDays,
                    });
                } else {
                    // Show error instead of crashing
                    setError(plans[0].error || 'Lesson generation returned incomplete data');
                }
            }
        } catch (err) {
            // Profile incomplete — surface a clear action path
            const errDetail = err.response?.data?.detail;
            const backendFailureCode = err.failure_code || err.response?.data?.failure_code;
            const backendRef = err.request_id || err.response?.data?.request_id || 'unknown';
            if (err.response?.status === 422 && errDetail?.error === 'profile_incomplete') {
                setError('profile_incomplete');
            } else if (backendFailureCode === 'PROFILE_INCOMPLETE') {
                setError('profile_incomplete');
            } else {
                const friendlyMsg = _getTeacherFriendlyError(backendFailureCode, err.message);
                setError(`${friendlyMsg} Ref: ${backendRef}`);
            }
            trackEvent('generation_failed', {
                generation_type: 'lesson',
                error: err.message || 'unknown',
                failure_code: backendFailureCode || 'unknown',
                request_id: backendRef,
            });
        } finally {
            setIsLoading(false);
            setGeneratingDayLabel('');
        }
    };





    const closePipeline = () => {
        setActivePipeline(null);
        setResult(null);
        setError(null);
        setLessonPlans([]);
        setActionError(null);
        setGeneratingSlides(false);
        setGeneratingPacket(false);
        setGeneratingQuiz(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showToast('Copied to clipboard!', 'success');
    };

    // v2 Sprint 2 — Plan Tomorrow handler
    const handleGenerateNextDay = () => {
        // Extract previous day's activities from the current lesson for continuity
        const currentLessonData = lastGeneratedResult?.data;
        let prevActivities = null;
        if (currentLessonData?.lesson_flow) {
            prevActivities = currentLessonData.lesson_flow
                .map(item => item.activity)
                .filter(Boolean);
        }

        // Calculate next weekday from current lessonDate
        const current = new Date(lessonDate + 'T00:00:00');
        let next = new Date(current);
        next.setDate(next.getDate() + 1);
        // Skip weekends
        while (next.getDay() === 0 || next.getDay() === 6) {
            next.setDate(next.getDate() + 1);
        }
        const nextDateStr = next.toISOString().split('T')[0];

        // Store context for the next generation cycle
        setNextDayContext({ previous_day_activities: prevActivities });
        setLessonDate(nextDateStr);
        setShowPostGenerationView(false);
        setLastGeneratedResult(null);
        setResult(null);
        setLessonPlans([]);
        setActivePipeline('lesson');
        // Flag auto-generate so useEffect triggers handleGenerateLesson
        setAutoGenerate(true);
        trackEvent('plan_tomorrow_clicked');
    };

    // Pre-fetch calendar events for the current week + next month on mount
    // (InstructionalCalendar shows 4 weeks, so we need coverage for surrounding months)
    useEffect(() => {
        if (!userProfile?.email) return;

        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        // Also pre-fetch next month if we're in the last 2 weeks of the month
        if (today.getDate() > 14) {
            const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
            const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

            const fetchNextMonth = async () => {
                try {
                    const response = await api.post('/api/production/calendar-events', {
                        teacher_id: userProfile.email,
                        year: nextYear,
                        month: nextMonth + 1,
                    });
                    if (response.data?.events) {
                        setCalendarEvents((prev) => ({ ...prev, ...response.data.events }));
                    }
                } catch (err) {
                    console.error(`Failed to pre-fetch next month events:`, err);
                }
            };
            fetchNextMonth();
        }
    }, [userProfile?.email]);

    const renderTeacherWorkspacePanel = () => {
        if (activeTeacherSidebarTab === 'classes') {
            return (
                <TeacherClassesWorkspace
                    operatingState={teacherOperatingState}
                    onGenerateLesson={handleGenerateCourseLesson}
                    onOpenGradingStudio={() => setShowGradingStudio(true)}
                />
            );
        }

        if (activeTeacherSidebarTab === 'resources') {
            return (
                <TeacherResourcesWorkspace
                    savedDocuments={savedDocuments}
                    contextDocuments={profileContextDocuments}
                    loading={resourcesLoading}
                    error={resourcesError}
                    operatingState={teacherOperatingState}
                    onOpenSavedLessons={() => setShowSavedLessons(true)}
                    onOpenAccountSettings={() => setShowAccountSettings(true)}
                />
            );
        }

        if (activeTeacherSidebarTab === 'feedback') {
            return (
                <UnavailableWorkspace
                    title="Feedback"
                    message="Feedback is temporarily unavailable. This will be restored in a follow-up fix."
                />
            );
        }

        return (
            <>
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="mb-8"
                >
                    <p className="text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Teacher Home</p>
                    <h1 className="mt-2 max-w-3xl text-3xl font-semibold leading-tight text-slate-950 sm:text-4xl">
                        {greetingText}
                    </h1>
                    <p className="mt-2 text-xl font-medium text-slate-600 dark:text-slate-300">
                        {teacherHomeReadyLine}
                    </p>
                </motion.div>

                <TeacherOperatingCenter
                    operatingState={teacherOperatingState}
                    onGenerateLesson={handleGenerateCourseLesson}
                    onOpenGradingStudio={() => setShowGradingStudio(true)}
                    onOpenResources={() => setActiveTeacherSidebarTab('resources')}
                />
            </>
        );
    };

    // Grading Studio — renders full-screen replacing dashboard
    if (showGradingStudio) {
        return (
            <GradingStudio
                userProfile={userProfile}
                isDark={isDark}
                onClose={() => setShowGradingStudio(false)}
            />
        );
    }

    // v2 Sprint 2 — When PostGenerationView is active, render it instead of the dashboard
    if (showPostGenerationView && lastGeneratedResult) {
        return (
            <PostGenerationView
                result={lastGeneratedResult}
                userProfile={userProfile}
                selectedSubject={selectedSubject}
                gradeLevel={gradeLevel}
                lessonDate={lessonDate}
                onClose={() => {
                    setShowPostGenerationView(false);
                    setLastGeneratedResult(null);
                    setResult(null);
                    setLessonPlans([]);
                }}
                onPlanNextDay={handleGenerateNextDay}
                onOpenClassroomBoard={(slideData) => {
                    setShowPostGenerationView(false);
                    setLessonData(slideData);
                    setShowClassroomBoard(true);
                }}
                onLessonSaved={() => {
                    if (userProfile?.email) {
                        api.get(`/api/documents/list?teacher_id=${encodeURIComponent(userProfile.email)}`)
                            .then(res => setSavedDocuments(res.data || []))
                            .catch(console.error);
                    }
                }}
            />
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <TeacherWorkspaceHeader
                selectedSubject={selectedSubject}
                gradeLevel={gradeLevel}
                lastGeneratedResult={lastGeneratedResult}
                subjects={subjects}
                onSelectSubject={setSelectedSubject}
                isDark={isDark}
                onToggleTheme={toggleTheme}
                showAssistantDrawer={showAssistantDrawer}
                onOpenAssistant={openAssistantDrawer}
                activePipeline={activePipeline}
                onToggleFeedback={() => {
                    setShowAssistantDrawer(false);
                    setActivePipeline(null);
                    setActiveTeacherSidebarTab('feedback');
                }}
                onOpenSavedLessons={() => setShowSavedLessons(true)}
                isAdmin={isAdmin}
                hasDevCenterAccess={hasDevCenterAccess}
                onEnterAdminWorkspace={onEnterAdminWorkspace}
                onEnterDevCenter={onEnterDevCenter}
                showAdminMenu={showAdminMenu}
                onToggleAdminMenu={() => setShowAdminMenu(!showAdminMenu)}
                onCloseAdminMenu={() => setShowAdminMenu(false)}
                onOpenAccountSettings={() => setShowAccountSettings(true)}
                onSwitchToAssistant={null}
                onShowLogoutConfirm={() => setShowLogoutConfirm(true)}
                onReturnDashboard={() => {
                    setActivePipeline(null);
                    setActiveTeacherSidebarTab('home');
                    setShowPostGenerationView(false);
                    setResult(null);
                    setLessonPlans([]);
                    setError(null);
                }}
            />

            <div className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8">
                {/* Email Verification Banner — suppressed for beta (Sprint 1 v2 simplification) */}
                {/* Backend route /send-verification remains intact */}

                {/* ── ROLE-AWARE PRIMARY ACTION AREA ── */}
                {!activePipeline && (
                    <div className="space-y-6">

                        {/* ═══════ ADMIN WORKSPACE ═══════ */}
                        {showAdminWorkspace && (
                            <AdminPriorityWorkspace
                                onEnterAdminWorkspace={onEnterAdminWorkspace}
                                onOpenAssistant={openAssistantDrawer}
                            />
                        )}

                        {/* ═══════ COUNSELOR WORKSPACE ═══════ */}
                        {userRole === 'counselor' && (
                            <CounselorPriorityWorkspace
                                onOpenAssistant={openAssistantDrawer}
                                onOpenSettings={() => setShowAccountSettings(true)}
                            />
                        )}

                        {/* ═══════ SUPPORT STAFF WORKSPACE ═══════ */}
                        {userRole === 'support_staff' && (
                            <SupportStaffPriorityWorkspace
                                onOpenAssistant={openAssistantDrawer}
                            />
                        )}

                        {/* ═══════ Campus Intelligence Hub — PRIMARY for non-teachers ═══════ */}
                        {userRole !== 'teacher' && (
                            <CampusIntelligenceHub
                                userRole={userRole}
                                prominence="primary"
                            />
                        )}

                        {/* ═══════ TEACHER HOME DASHBOARD (inactive landing only) ═══════ */}
                        {showTeacherWorkspace && (
                            <div className="overflow-hidden rounded-[28px] bg-[#f5f5f7] shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:bg-[#10141b] dark:shadow-[0_24px_70px_rgba(2,6,23,0.42)]">
                                <div className="flex min-h-[720px] flex-col lg:flex-row">
                                    <aside className="border-b border-white/70 bg-white/80 p-4 dark:border-slate-700/50 dark:bg-[#171d26] lg:w-64 lg:border-b-0 lg:border-r">
                                        <div className="mb-6 flex items-center gap-3 px-2">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFC107] text-base font-bold text-[#1d1d1f] shadow-sm">
                                                S
                                            </div>
                                            <div>
                                                <div className="text-lg font-semibold tracking-tight text-slate-950">Saige</div>
                                                <div className="text-xs font-medium text-slate-500 dark:text-slate-300">Teacher OS</div>
                                            </div>
                                        </div>
                                        <nav className="grid gap-1 sm:grid-cols-2 lg:grid-cols-1">
                                            {TEACHER_SIDEBAR_ITEMS.map((item) => {
                                                const isActive = item.id === 'assistant'
                                                    ? showAssistantDrawer
                                                    : activeTeacherSidebarTab === item.id;

                                                return (
                                                    <button
                                                        key={item.id}
                                                        type="button"
                                                        onClick={() => handleTeacherSidebarClick(item)}
                                                        disabled={item.disabled}
                                                        aria-current={isActive ? 'page' : undefined}
                                                        className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-medium transition-colors ${isActive
                                                            ? 'bg-[#FFC107] text-[#1d1d1f] shadow-[0_8px_20px_rgba(255,193,7,0.28)]'
                                                            : item.disabled
                                                                ? 'cursor-not-allowed text-slate-400 dark:text-slate-600'
                                                                : 'text-slate-600 hover:bg-white hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                                                        }`}
                                                    >
                                                        {React.createElement(item.icon, { size: 17 })}
                                                        <span>{item.label}</span>
                                                        {item.badge && (
                                                            <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive
                                                                ? 'bg-black/10 text-[#1d1d1f]'
                                                                : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'
                                                            }`}>
                                                                {item.badge}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </nav>
                                    </aside>

                                    <main className="flex-1 p-4 sm:p-6 lg:p-8">
                                        <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-white/80 bg-white/85 px-5 py-4 shadow-[0_12px_35px_rgba(15,23,42,0.05)] dark:border-slate-700/60 dark:bg-[#171d26]/95 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFC107] text-sm font-bold text-[#1d1d1f]">
                                                    S
                                                </div>
                                                <div>
                                                    <div className="text-lg font-semibold text-slate-950">Saige</div>
                                                    <div className="text-xs font-medium text-slate-500 dark:text-slate-300">Teacher home</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition-colors hover:text-slate-950"
                                                    aria-label="Notifications"
                                                >
                                                    <Bell size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAccountSettings(true)}
                                                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 text-left transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-500"
                                                >
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-sm font-bold text-amber-700 dark:bg-amber-400/18 dark:text-amber-200">
                                                        {teacherFirstName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="hidden sm:block">
                                                        <div className="text-sm font-semibold text-slate-950">{teacherFirstName}</div>
                                                        <div className="text-[11px] font-medium text-slate-500 dark:text-slate-300">Teacher</div>
                                                    </div>
                                                    <ChevronDown size={16} className="text-slate-500 dark:text-slate-300" />
                                                </button>
                                            </div>
                                        </div>

                                        {renderTeacherWorkspacePanel()}
                                    </main>
                                </div>
                            </div>
                        )}
                    </div>
                )
                }

                {/* Day Detail Modal */}
                <AnimatePresence>
                    {selectedDay && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                            onClick={() => setSelectedDay(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white rounded-2xl p-6 shadow-[0_12px_40px_rgba(0,0,0,0.12)] max-w-md w-full"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-[#1d1d1f]">
                                        {new Date(selectedDay.dateStr + 'T00:00:00').toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </h3>
                                    <button
                                        onClick={() => setSelectedDay(null)}
                                        className="text-[#bbb] hover:text-[#666]"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Unit info */}
                                {selectedDay.unit_name && (
                                    <div className="mb-4 p-3 bg-amber-50 rounded-xl">
                                        <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                                            <Target size={16} />
                                            <span>Unit: {selectedDay.unit_name}</span>
                                        </div>
                                        <p className="text-sm text-amber-600">
                                            Day {selectedDay.unit_day} of unit
                                        </p>
                                    </div>
                                )}

                                {/* Lesson objective */}
                                {selectedDay.lesson_title && (
                                    <div className="mb-4 p-3 bg-[#fafafa] rounded-xl">
                                        <div className="flex items-center gap-2 text-[#444] font-medium mb-1">
                                            <BookOpen size={16} />
                                            <span>Today's Focus</span>
                                        </div>
                                        <p className="text-sm text-[#666]">
                                            {selectedDay.lesson_title}
                                        </p>
                                    </div>
                                )}

                                {/* Special day info */}
                                {selectedDay.special_day && (
                                    <div className={`mb-4 p-3 rounded-xl ${selectedDay.special_day.type === 'test' || selectedDay.special_day.type === 'staar'
                                        ? 'bg-red-50'
                                        : selectedDay.special_day.type === 'half_day'
                                            ? 'bg-orange-50'
                                            : 'bg-blue-50'
                                        }`}>
                                        <div className={`flex items-center gap-2 font-medium mb-1 ${selectedDay.special_day.type === 'test' || selectedDay.special_day.type === 'staar'
                                            ? 'text-red-700'
                                            : selectedDay.special_day.type === 'half_day'
                                                ? 'text-orange-700'
                                                : 'text-blue-700'
                                            }`}>
                                            <Clock size={16} />
                                            <span>{selectedDay.special_day.name}</span>
                                        </div>
                                        <p className={`text-sm ${selectedDay.special_day.type === 'test' || selectedDay.special_day.type === 'staar'
                                            ? 'text-red-600'
                                            : selectedDay.special_day.type === 'half_day'
                                                ? 'text-orange-600'
                                                : 'text-blue-600'
                                            }`}>
                                            Schedule: {selectedDay.special_day.schedule}
                                        </p>
                                    </div>
                                )}

                                {/* Reteach Toggle */}
                                <div className="mb-4 p-3 bg-rose-50 rounded-xl">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <RotateCcw size={16} className="text-rose-600" />
                                            <span className="text-sm font-medium text-rose-700">Flag for Reteach</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const isCurrentlyFlagged = !!reteachFlags[selectedDay.dateStr];
                                                const newFlagged = !isCurrentlyFlagged;

                                                // Optimistic update — move toggle immediately
                                                const previousFlags = { ...reteachFlags };
                                                setReteachFlags(prev => {
                                                    if (newFlagged) {
                                                        return { ...prev, [selectedDay.dateStr]: { flagged: true } };
                                                    } else {
                                                        const { [selectedDay.dateStr]: _, ...rest } = prev;
                                                        return rest;
                                                    }
                                                });

                                                try {
                                                    await api.post('/api/production/reteach-flag', {
                                                        teacher_id: userProfile?.email,
                                                        date: selectedDay.dateStr,
                                                        flagged: newFlagged
                                                    });
                                                } catch (err) {
                                                    console.error('Failed to toggle reteach flag:', err);
                                                    // Revert on error
                                                    setReteachFlags(previousFlags);
                                                }
                                            }}
                                            style={{
                                                position: 'relative',
                                                width: '48px',
                                                height: '24px',
                                                borderRadius: '12px',
                                                backgroundColor: reteachFlags[selectedDay.dateStr] ? '#f43f5e' : '#cbd5e1',
                                                border: 'none',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s ease',
                                                padding: 0,
                                                flexShrink: 0
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '2px',
                                                    left: reteachFlags[selectedDay.dateStr] ? '26px' : '2px',
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                    transition: 'left 0.2s ease'
                                                }}
                                            />
                                        </button>
                                    </div>
                                    {reteachFlags[selectedDay.dateStr] && (
                                        <p className="text-xs text-rose-600 mt-2">🚩 Marked for spiral review</p>
                                    )}
                                </div>

                                {/* Saved Lessons for this day */}
                                <div className="mb-4">
                                    <h4 className="text-sm font-semibold text-[#444] mb-2 flex items-center gap-2">
                                        <FolderOpen size={14} />
                                        Saved Lessons
                                    </h4>
                                    {dayDocsLoading ? (
                                        <div className="text-xs text-[#bbb] py-2">Loading...</div>
                                    ) : dayDocuments.length === 0 ? (
                                        <div className="text-xs text-[#bbb] py-2 bg-[#fafafa] rounded-lg px-3">
                                            No saved lessons for this date
                                        </div>
                                    ) : (
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {dayDocuments.map(doc => (
                                                <div key={doc.id} className="bg-[#fafafa] rounded-lg border border-black/5 overflow-hidden">
                                                    <button
                                                        onClick={() => setExpandedDayDoc(expandedDayDoc === doc.id ? null : doc.id)}
                                                        className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-[#f5f5f7] transition-colors"
                                                    >
                                                        <span className="text-xs font-medium text-[#444] truncate">{doc.title}</span>
                                                        <ChevronRight size={14} className={`text-[#bbb] transition-transform ${expandedDayDoc === doc.id ? 'rotate-90' : ''}`} />
                                                    </button>
                                                    {expandedDayDoc === doc.id && (
                                                        <div className="px-3 pb-2">
                                                            <pre className="text-[10px] text-[#666] whitespace-pre-wrap bg-white p-2 rounded border border-black/5 max-h-32 overflow-auto">
                                                                {doc.content}
                                                            </pre>
                                                            <button
                                                                onClick={() => { navigator.clipboard.writeText(doc.content); showToast('Copied!', 'success'); }}
                                                                className="mt-1 text-[10px] text-amber-600 hover:text-amber-700"
                                                            >
                                                                Copy to clipboard
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Action buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setLessonDate(selectedDay.dateStr);
                                            setSelectedDay(null);
                                            setActivePipeline('lesson');
                                        }}
                                        className="flex-1 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <BookOpen size={16} />
                                        Plan This Day
                                    </button>
                                    <button
                                        onClick={() => setSelectedDay(null)}
                                        className="px-4 py-2 border border-black/5 text-[#666] rounded-xl hover:bg-[#fafafa] transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ==================== LESSON PIPELINE ==================== */}
                <AnimatePresence>
                    {activePipeline === 'lesson' && !result && lessonPlans.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-black/5"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <h2 className="text-xl font-semibold text-[#1d1d1f]">{getDatePrompt(lessonDate, numDays)}</h2>
                                    <p className="text-sm text-[#999]">{numDays > 1 ? `Pick a topic and we'll build ${numDays} days of lessons.` : 'Pick a topic and we\'ll build the lesson.'}</p>
                                </div>
                                <button onClick={closePipeline} className="text-[#bbb] hover:text-[#666] transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Date — subtle inline chip */}
                                <div className="flex items-center gap-2">
                                    <Calendar size={14} className="text-[#999]" />
                                    <input
                                        type="date"
                                        value={lessonDate}
                                        onChange={(e) => {
                                            const selectedDate = new Date(e.target.value + 'T00:00:00');
                                            const dayOfWeek = selectedDate.getDay();

                                            if (dayOfWeek === 0 || dayOfWeek === 6) {
                                                const daysUntilMonday = dayOfWeek === 0 ? 1 : 2;
                                                selectedDate.setDate(selectedDate.getDate() + daysUntilMonday);
                                                const mondayStr = selectedDate.toISOString().split('T')[0];
                                                setLessonDate(mondayStr);
                                                setError('Weekend selected — advanced to next Monday');
                                                setTimeout(() => setError(null), 3000);
                                            } else {
                                                setLessonDate(e.target.value);
                                                setError(null);
                                            }
                                        }}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="px-3 py-1.5 rounded-lg border border-black/5 text-sm text-[#444] focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                                    />
                                </div>

                                {/* Primary textarea — "What are you teaching?" */}
                                <div>
                                    <label className="block text-sm font-medium text-[#444] mb-1.5">What are you teaching?</label>
                                    <textarea
                                        value={lessonTopics}
                                        onChange={(e) => { setLessonTopics(e.target.value); if (showTopicHint) setShowTopicHint(false); }}
                                        placeholder={"Leave blank to continue your scope.\nOr type: Chapter 5 ratios, review day, lab, reteach, etc."}
                                        className="w-full px-3 py-2.5 rounded-xl border border-black/5 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none text-sm"
                                        rows={3}
                                    />
                                    {showTopicHint && (
                                        <p className="text-xs text-amber-700 mt-1.5 flex items-start gap-1">
                                            <span className="mt-0.5">💡</span>
                                            <span>Try adding a more specific topic, objective, standard, or activity.</span>
                                        </p>
                                    )}
                                </div>

                                {/* Collapsible Advanced Options */}
                                <div>
                                    <button
                                        onClick={() => setShowMoreOptions(!showMoreOptions)}
                                        className="flex items-center gap-1.5 text-xs font-medium text-[#999] hover:text-[#666] transition-colors"
                                    >
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform duration-200 ${showMoreOptions ? 'rotate-180' : ''}`}
                                        />
                                        Advanced options
                                    </button>

                                    {showMoreOptions && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 space-y-4 bg-[#fafafa] rounded-xl p-4 border border-black/5"
                                        >
                                            {/* Student Support — simple dropdown */}
                                            <div>
                                                <label className="text-xs font-medium text-[#666] mb-1.5 block">Student Support Focus</label>
                                                <select
                                                    value={differentiationFocus}
                                                    onChange={(e) => {
                                                        setDifferentiationFocus(e.target.value);
                                                    }}
                                                    className="w-full px-3 py-2 rounded-lg border border-black/5 text-sm text-[#444] focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
                                                >
                                                    <option value="">None — general audience</option>
                                                    <option value="ell">ELL / Multilingual Learners</option>
                                                    <option value="iep_504">IEP / 504 Accommodations</option>
                                                    <option value="gifted">Gifted & Advanced</option>
                                                    <option value="below_grade">Below Grade Level</option>
                                                </select>
                                                <p className="text-[10px] text-[#bbb] mt-1">Adds scaffolding and differentiation strategies for the selected group.</p>
                                            </div>

                                            {/* Number of Days */}
                                            <div>
                                                <label className="text-xs font-medium text-[#666] mb-1.5 block">Plan Multiple Days</label>
                                                <div className="flex gap-2">
                                                    {[1, 2, 3, 4, 5].map(n => (
                                                        <button
                                                            key={n}
                                                            onClick={() => setNumDays(n)}
                                                            className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all duration-150 ${numDays === n
                                                                ? 'bg-amber-100 text-amber-700 border-2 border-amber-400'
                                                                : 'bg-white text-[#666] border border-black/10 hover:border-amber-300 hover:text-amber-600'
                                                            }`}
                                                        >
                                                            {n}
                                                        </button>
                                                    ))}
                                                    <span className="text-xs text-[#999] self-center ml-1">{numDays === 1 ? 'day' : 'days'}</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                            </div>

                            {(() => {
                                const readinessSummary = getReadinessSummary(lessonReadiness, lessonTopics, userProfile);
                                const readinessItems = buildReadinessItems(lessonReadiness, userProfile);
                                const summaryToneClass = readinessSummary.tone === 'success'
                                    ? 'bg-emerald-50 border-emerald-200'
                                    : readinessSummary.tone === 'needs_topic'
                                        ? 'bg-amber-50 border-amber-300'
                                        : 'bg-amber-50 border-amber-200';
                                const summaryBadgeClass = readinessSummary.tone === 'success'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : readinessSummary.tone === 'needs_topic'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-amber-100 text-amber-700';

                                return (
                                    <div
                                        className={`rounded-xl border p-4 ${summaryToneClass}`}
                                        data-testid="lesson-readiness-panel"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-medium text-[#1d1d1f]">
                                                    {lessonReadinessLoading ? 'Checking lesson readiness…' : readinessSummary.title}
                                                </p>
                                                <p className="text-xs text-[#666] mt-1">
                                                    {lessonReadinessLoading
                                                        ? 'Reviewing required inputs and optional supports.'
                                                        : readinessSummary.detail}
                                                </p>
                                            </div>
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${summaryBadgeClass}`}>
                                                {readinessSummary.canGenerate ? 'Ready' : readinessSummary.tone === 'needs_topic' ? 'Needs topic' : 'Needs setup'}
                                            </span>
                                        </div>

                                        <div className="mt-3 grid sm:grid-cols-2 gap-2.5">
                                            {readinessItems.map((item) => {
                                                const chipClass = item.meta.tone === 'success'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : item.meta.tone === 'warning'
                                                        ? 'bg-amber-100 text-amber-700'
                                                        : 'bg-slate-100 text-slate-600';
                                                return (
                                                    <div
                                                        key={item.key}
                                                        className="rounded-lg bg-white/80 border border-black/5 px-3 py-2"
                                                        data-testid={`readiness-${item.key}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`w-2 h-2 rounded-full ${item.meta.dot}`} />
                                                            <span className="text-xs font-medium text-[#444]">{item.label}</span>
                                                            <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${chipClass}`}>
                                                                {item.meta.label}
                                                            </span>
                                                        </div>
                                                        <p className="text-[11px] text-[#777] mt-1.5 leading-relaxed">
                                                            {item.detail}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {!!lessonReadiness?.warnings?.length && (
                                            <div className="mt-3 space-y-1" data-testid="lesson-readiness-warnings">
                                                {lessonReadiness.warnings.map((warning, index) => (
                                                    <p key={`${warning}-${index}`} className="text-[11px] text-[#8b5e00]">
                                                        {warning}
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                                {error && error === 'profile_incomplete' ? (
                                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                                        <p className="text-amber-800 font-medium text-sm mb-2">
                                            Complete your profile to generate lessons
                                        </p>
                                        <p className="text-amber-600 text-xs mb-3">
                                            Subject and grade level are required for Saige to create accurate lesson plans.
                                        </p>
                                        <button
                                            onClick={() => { setShowAccountSettings(true); setError(null); }}
                                            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                                        >
                                            Open Account Settings
                                        </button>
                                    </div>
                                ) : error ? (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                                        {error}
                                    </div>
                                ) : null}

                                {/* Primary CTA — refined, less oversized */}
                                <button
                                    onClick={handleGenerateLesson}
                                    disabled={isLoading || (() => { const rs = getReadinessSummary(lessonReadiness, lessonTopics, userProfile); return !rs.canGenerate; })()}
                                    className="w-full py-3 bg-[#FFC107] text-black rounded-xl font-medium text-sm hover:bg-[#e6ad00] active:scale-[0.98] disabled:opacity-50 transition-all duration-200 flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            {generatingDayLabel || lessonProgressMsg}
                                        </>
                                    ) : (
                                        <>
                                            Generate Lesson
                                            <ArrowRight size={16} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Multi-day Lesson Results */}
                <AnimatePresence>
                    {lessonPlans.length > 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-4"
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-[#1d1d1f]">📚 {lessonPlans.length}-Day Lesson Plan</h2>
                                <button onClick={closePipeline} className="text-[#bbb] hover:text-[#666]">
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Accept & Generate All — REMOVED in v2 Sprint 2. Deliverables are now on-demand. */}

                            {actionError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center justify-between">
                                    <span>{actionError}</span>
                                    <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600">
                                        <X size={16} />
                                    </button>
                                </div>
                            )}

                            {lessonPlans.map((plan, idx) => (
                                <motion.div
                                    key={plan.date}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.06)] border border-black/5"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-bold text-[#444]">
                                            Day {idx + 1}: {(() => {
                                                const [y, m, d] = plan.date.split('-').map(Number);
                                                return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                            })()}
                                        </h3>
                                        <button
                                            onClick={() => copyToClipboard(plan.text || '')}
                                            className="px-3 py-1 text-xs bg-[#f5f5f7] text-[#666] rounded-lg hover:bg-[#eee] transition-colors flex items-center gap-1"
                                        >
                                            <Download size={14} />
                                            Copy
                                        </button>
                                    </div>
                                    <pre className="whitespace-pre-wrap font-mono text-xs text-[#444] leading-relaxed bg-[#fafafa] p-4 rounded-xl overflow-auto max-h-60">
                                        {plan.text}
                                    </pre>

                                    {/* Per-day action buttons */}
                                    <div className="mt-4 grid grid-cols-3 gap-2">
                                        <button
                                            onClick={async () => {
                                                setGeneratingSlides(true);
                                                setActionError(null);
                                                try {
                                                    const response = await api.post('/api/production/generate-slides', {
                                                        lesson_content: plan.text || '',
                                                        lesson_data: plan.data || {},
                                                        teacher_id: userProfile?.email,
                                                        subject: selectedSubject,
                                                        grade_level: gradeLevel
                                                    });
                                                    if (response.data?.slides) {
                                                        setLessonData(response.data);
                                                        setLessonId(response.data.lesson_id);
                                                        setShowClassroomBoard(true);
                                                    }
                                                } catch (err) {
                                                    setActionError(`Failed to generate slides for Day ${idx + 1}`);
                                                    console.error(err);
                                                } finally {
                                                    setGeneratingSlides(false);
                                                }
                                            }}
                                            disabled={generatingSlides || generatingPacket || generatingQuiz}
                                            className="flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-br from-orange-400 to-red-500 text-white rounded-xl text-sm font-medium hover:from-orange-500 hover:to-red-600 transition-all disabled:opacity-50 shadow"
                                        >
                                            <Monitor size={16} />
                                            Open Lesson Board
                                        </button>
                                        <button
                                            onClick={async () => {
                                                setGeneratingPacket(true);
                                                setActionError(null);
                                                try {
                                                    const [y, m, d] = plan.date.split('-').map(Number);
                                                    const dateFormatted = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                                    await downloadFile('/api/production/generate-student-packet', {
                                                        lesson_content: plan.text || '',
                                                        subject: selectedSubject,
                                                        grade_level: gradeLevel,
                                                        output_format: 'docx',
                                                        teacher_id: userProfile?.email,
                                                        lesson_title: `${selectedSubject} - ${dateFormatted}`
                                                    }, `Student_Packet_Day${idx + 1}_${selectedSubject}.docx`);
                                                } catch (err) {
                                                    setActionError(`Failed to generate packet for Day ${idx + 1}`);
                                                    console.error(err);
                                                } finally {
                                                    setGeneratingPacket(false);
                                                }
                                            }}
                                            disabled={generatingSlides || generatingPacket || generatingQuiz}
                                            className="flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-br from-amber-300 to-yellow-500 text-[#1d1d1f] rounded-xl text-sm font-medium hover:from-amber-400 hover:to-yellow-600 transition-all disabled:opacity-50 shadow"
                                        >
                                            <FileText size={16} />
                                            Print
                                        </button>
                                        <button
                                            onClick={async () => {
                                                setGeneratingQuiz(true);
                                                setActionError(null);
                                                try {
                                                    const [y, m, d] = plan.date.split('-').map(Number);
                                                    const dateFormatted = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                                    const response = await api.post('/api/production/generate-assessment', {
                                                        lesson_content: plan.text || '',
                                                        subject: selectedSubject,
                                                        grade_level: gradeLevel,
                                                        num_questions: 10,
                                                        teacher_id: userProfile?.email,
                                                        lesson_title: `${selectedSubject} - ${dateFormatted}`
                                                    });
                                                    if (response.data?.questions) {
                                                        setQuizData(response.data);
                                                        setShowQuizModal(true);
                                                    }
                                                } catch (err) {
                                                    setActionError(`Failed to generate quiz for Day ${idx + 1}`);
                                                    console.error(err);
                                                } finally {
                                                    setGeneratingQuiz(false);
                                                }
                                            }}
                                            disabled={generatingSlides || generatingPacket || generatingQuiz}
                                            className="flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-br from-purple-400 to-indigo-500 text-white rounded-xl text-sm font-medium hover:from-purple-500 hover:to-indigo-600 transition-all disabled:opacity-50 shadow"
                                        >
                                            <ClipboardCheck size={16} />
                                            Assess
                                        </button>
                                    </div>
                                </motion.div>
                            ))}

                            <div className="flex gap-3">
                                <button
                                    onClick={saveAllLessonPlans}
                                    disabled={savingDocument}
                                    className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <Save size={18} />
                                    {savingDocument ? 'Saving...' : 'Save All'}
                                </button>
                                <button
                                    onClick={closePipeline}
                                    className="flex-1 py-3 bg-[#1d1d1f] text-white rounded-xl font-medium hover:bg-[#333] transition-colors"
                                >
                                    Done
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>




                {/* ==================== MY DOCUMENTS MODAL ==================== */}
                <AnimatePresence>
                    {showMyDocuments && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                            onClick={() => setShowMyDocuments(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white rounded-2xl p-6 shadow-[0_12px_40px_rgba(0,0,0,0.12)] max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-amber-300 to-yellow-500 rounded-xl flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                                            <FolderOpen className="text-[#1d1d1f]" size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-[#1d1d1f]">My Lessons</h3>
                                            <p className="text-xs text-[#999]">{savedDocuments.length} saved documents</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowMyDocuments(false)}
                                        className="text-[#bbb] hover:text-[#666]"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-3">
                                    {savedDocuments.length === 0 ? (
                                        <div className="text-center py-12 text-[#999]">
                                            <FolderOpen size={48} className="mx-auto mb-4 opacity-30" />
                                            <p>No saved lessons yet</p>
                                            <p className="text-sm mt-1">Generate a lesson and click "Save All" to save it here</p>
                                        </div>
                                    ) : (
                                        savedDocuments.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className="bg-[#fafafa] rounded-xl p-4 border border-black/5 hover:border-slate-300 transition-colors"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-[#1d1d1f]">{doc.title}</h4>
                                                        <p className="text-xs text-[#999] mt-1">
                                                            {new Date(doc.created_at).toLocaleDateString('en-US', {
                                                                weekday: 'short',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(doc.content);
                                                                showToast('Copied to clipboard!', 'success');
                                                            }}
                                                            className="px-3 py-1 text-xs bg-white border border-black/5 text-[#666] rounded-lg hover:bg-[#f5f5f7]"
                                                        >
                                                            Copy
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                if (confirm('Delete this lesson?')) {
                                                                    try {
                                                                        await api.delete(`/api/documents/${doc.id}`, {
                                                                            params: { teacher_id: userProfile?.email }
                                                                        });
                                                                        fetchMyDocuments();
                                                                    } catch (err) {
                                                                        console.error('Error deleting:', err);
                                                                    }
                                                                }
                                                            }}
                                                            className="px-3 py-1 text-xs bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                                <pre className="mt-3 text-xs text-[#666] whitespace-pre-wrap bg-white p-3 rounded-lg border border-black/5 max-h-32 overflow-auto">
                                                    {doc.content.substring(0, 500)}{doc.content.length > 500 ? '...' : ''}
                                                </pre>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <button
                                    onClick={() => setShowMyDocuments(false)}
                                    className="mt-4 w-full py-3 bg-[#1d1d1f] text-white rounded-xl font-medium hover:bg-[#333] transition-colors"
                                >
                                    Close
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence >

            </div >

            {/* ==================== ONBOARDING MODAL ==================== */}
            < AnimatePresence >
                {showOnboarding && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
                        onClick={() => {
                            // Only allow dismissing during step 2 (curriculum upload is optional)
                            if (onboardingStep === 2) {
                                setShowOnboarding(false);
                                localStorage.setItem('saige_onboarding_complete', 'true');
                            }
                            // Step 1 (grade/subject) is mandatory - cannot dismiss
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl p-8 shadow-[0_12px_40px_rgba(0,0,0,0.12)] max-w-md w-full cursor-default"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                                    {onboardingStep === 1 ? (
                                        <GraduationCap className="text-white" size={24} />
                                    ) : (
                                        <FileText className="text-white" size={24} />
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-[#1d1d1f]">
                                        {onboardingStep === 1 ? 'Welcome to Saige!' : 'Add Your Curriculum'}
                                    </h3>
                                    <p className="text-sm text-[#999]">
                                        {onboardingStep === 1
                                            ? 'Junior High Edition — Step 1 of 2'
                                            : 'Step 2 of 2 — Upload your scope & sequence'}
                                    </p>
                                </div>
                            </div>

                            {/* Step indicator */}
                            <div className="flex gap-2 mb-6">
                                <div className={`h-1.5 flex-1 rounded-full ${onboardingStep >= 1 ? 'bg-amber-500' : 'bg-[#eee]'}`} />
                                <div className={`h-1.5 flex-1 rounded-full ${onboardingStep >= 2 ? 'bg-amber-500' : 'bg-[#eee]'}`} />
                            </div>

                            {onboardingStep === 1 ? (
                                <>
                                    {/* Step 1: Grade Level */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-[#444] mb-2">
                                            What grade do you teach?
                                        </label>
                                        <div className="grid grid-cols-3 gap-3">
                                            {['6th', '7th', '8th'].map(grade => (
                                                <button
                                                    key={grade}
                                                    onClick={() => setOnboardingGrade(grade)}
                                                    className={`py-3 rounded-xl text-base font-semibold transition-all ${onboardingGrade === grade
                                                        ? 'bg-amber-500 text-white shadow-md scale-105'
                                                        : 'bg-[#f5f5f7] text-[#666] hover:bg-[#eee]'
                                                        }`}
                                                >
                                                    {grade} Grade
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Step 1: Subjects */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-[#444] mb-2">
                                            What subject area(s)? <span className="text-[#bbb]">(select all that apply)</span>
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {[
                                                { name: 'Math', icon: '📐' },
                                                { name: 'ELA', icon: '📚' },
                                                { name: 'Science', icon: '🔬' },
                                                { name: 'Social Studies', icon: '🌎' },
                                                { name: 'PE / Sports', icon: '🏀' },
                                                { name: 'Electives', icon: '🎨' }
                                            ].map(subject => (
                                                <button
                                                    key={subject.name}
                                                    onClick={() => toggleOnboardingSubject(subject.name)}
                                                    className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all flex items-center gap-3 ${onboardingSubjects.includes(subject.name)
                                                        ? 'bg-emerald-500 text-white shadow-md scale-[1.02]'
                                                        : 'bg-[#f5f5f7] text-[#666] hover:bg-[#eee]'
                                                        }`}
                                                >
                                                    <span className="text-lg">{subject.icon}</span>
                                                    {subject.name}
                                                    {onboardingSubjects.includes(subject.name) && <Check size={16} className="ml-auto" />}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleOnboardingStep1}
                                        disabled={savingOnboarding || !onboardingGrade || onboardingSubjects.length === 0}
                                        className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl font-medium shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {savingOnboarding ? (
                                            <>
                                                <Loader2 className="animate-spin" size={18} />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                Next: Add Curriculum
                                                <ArrowRight size={18} />
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {/* Step 2: Curriculum Upload */}
                                    <div className="mb-6">
                                        <p className="text-sm text-[#666] mb-4">
                                            Upload your <strong>scope & sequence</strong>, <strong>at-a-glance</strong>, or <strong>objectives document</strong> so Saige can understand your specific curriculum standards.
                                        </p>

                                        {/* File Drop Zone */}
                                        <div
                                            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${curriculumFile
                                                ? 'border-emerald-400 bg-emerald-50'
                                                : 'border-[#ddd] hover:border-amber-400 hover:bg-amber-50/30'
                                                }`}
                                            onClick={() => curriculumInputRef.current?.click()}
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const file = e.dataTransfer.files[0];
                                                if (file) setCurriculumFile(file);
                                            }}
                                        >
                                            <input
                                                ref={curriculumInputRef}
                                                type="file"
                                                accept=".pdf,.docx,.doc,.txt"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) setCurriculumFile(file);
                                                }}
                                            />
                                            {curriculumFile ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                                                        <FileText className="text-emerald-600" size={24} />
                                                    </div>
                                                    <p className="font-medium text-emerald-700">{curriculumFile.name}</p>
                                                    <p className="text-xs text-[#999]">Click to change file</p>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-12 h-12 bg-[#f5f5f7] rounded-xl flex items-center justify-center">
                                                        <Upload className="text-[#bbb]" size={24} />
                                                    </div>
                                                    <p className="font-medium text-[#666]">Drop your file here</p>
                                                    <p className="text-xs text-[#bbb]">or click to browse • PDF, Word, or Text</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3">
                                        <button
                                            onClick={finishOnboarding}
                                            className="flex-1 py-3 bg-[#f5f5f7] text-[#666] rounded-xl font-medium hover:bg-[#eee] transition-all"
                                        >
                                            Skip for now
                                        </button>
                                        <button
                                            onClick={handleCurriculumUpload}
                                            disabled={uploadingCurriculum}
                                            className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl font-medium shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {uploadingCurriculum ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={18} />
                                                    Processing...
                                                </>
                                            ) : curriculumFile ? (
                                                <>
                                                    <Upload size={18} />
                                                    Upload & Continue
                                                </>
                                            ) : (
                                                <>
                                                    <ArrowRight size={18} />
                                                    Get Started
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Back button */}
                                    <button
                                        onClick={() => setOnboardingStep(1)}
                                        className="w-full mt-3 py-2 text-sm text-[#999] hover:text-[#444] flex items-center justify-center gap-1"
                                    >
                                        <ChevronLeft size={16} />
                                        Back to Step 1
                                    </button>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence >

            {/* ==================== CLASSROOM BOARD MODAL ==================== */}
            < AnimatePresence >
                {showClassroomBoard && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50"
                    >

                        <TeacherControlPanel
                            userEmail={userProfile?.email}
                            lessonId={lessonId}
                            boardData={lessonData ? {
                                // Handle both lesson_flow (from lesson generation) and slides (from generate-slides API)
                                objectives: (lessonData.objectives || (lessonData.objective ? [lessonData.objective] : null))?.map(obj => ({
                                    text: validateObjective(typeof obj === 'string' ? obj : obj.text || obj),
                                    standard: ''
                                })) || [{ text: validateObjective(`I can demonstrate understanding of ${selectedSubject} concepts.`), standard: '' }],
                                // Use slides if lesson_flow is not available (generate-slides API returns 'slides')
                                agenda: (lessonData.lesson_flow || lessonData.slides)?.map(item => ({
                                    title: item.activity || item.title || 'Activity',
                                    content: item.content || item.bullets?.join(' • ') || item.teacher_actions || item.student_actions || '',
                                    duration: parseInt(item.time?.split('-')[1]) - parseInt(item.time?.split('-')[0]) || 10,
                                    mode: 'silent',
                                })) || [],
                                exitTicket: lessonData.assessment || 'What is one thing you learned today?',
                                materials: lessonData.materials || [],
                                // Use unit if unit_name is not available (generate-slides API returns 'unit')
                                unitName: lessonData.unit_name || lessonData.unit || '',
                                dayInUnit: lessonData.day_in_unit || 1
                            } : {
                                objectives: [{ text: `I can demonstrate mastery of ${selectedSubject} concepts.`, standard: '' }],
                                agenda: [
                                    { title: 'Bell Ringer', duration: 5, mode: 'silent' },
                                    { title: 'Direct Instruction', duration: 12, mode: 'listen' },
                                    { title: 'Guided Practice', duration: 10, mode: 'partner' },
                                    { title: 'Independent Work', duration: 15, mode: 'individual' },
                                    { title: 'Exit Ticket', duration: 5, mode: 'write' }
                                ],
                                exitTicket: 'What is one thing you learned today?'
                            }}
                            onExport={() => {
                                showToast('Export functionality coming soon!', 'info');
                            }}
                            onNavigateHome={() => {
                                setBoardMode('edit'); // reset mode when leaving board
                                setShowClassroomBoard(false);
                            }}
                            onDeleteSlides={() => {
                                if (window.confirm('Delete these slides? This will clear the current presentation.')) {
                                    setLessonData(null);
                                    setLessonId(null);
                                    setBoardMode('edit');
                                    setShowClassroomBoard(false);
                                    showToast('Slides deleted', 'success');
                                }
                            }}
                            boardMode={boardMode}
                            onBoardModeChange={setBoardMode}
                        />
                        {/* Close button — Edit-only. Hidden in Presenter and Display to prevent
                            accidental board exit during live teaching. */}
                        {boardMode === 'edit' && (
                            <button
                                onClick={() => { setBoardMode('edit'); setShowClassroomBoard(false); }}
                                className="fixed top-4 right-4 z-50 w-10 h-10 bg-slate-700 hover:bg-[#666] text-white rounded-full flex items-center justify-center transition-colors"
                                title="Back to Dashboard"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence >

            <TeacherWorkspaceOverlays
                showAccountSettings={showAccountSettings}
                onCloseAccountSettings={handleCloseAccountSettings}
                userProfile={userProfile}
                onUpdateProfile={(updated) => {
                    if (updated.grade_level) setGradeLevel(updated.grade_level);
                    if (updated.subjects) {
                        setSubjects(updated.subjects);
                        setSelectedSubject(updated.subjects[0] || '');
                    }
                }}
                showAdminDashboard={showAdminDashboard}
                isAdmin={isAdmin}
                onCloseAdminDashboard={() => setShowAdminDashboard(false)}
                showSavedLessons={showSavedLessons}
                onCloseSavedLessons={() => setShowSavedLessons(false)}
                onOpenSavedLesson={(lesson) => {
                    // Reopened lessons return to the canonical post-generation workspace.
                    setShowSavedLessons(false);
                    setLastGeneratedResult(lesson);
                    setResult(lesson);
                    setShowPostGenerationView(true);
                }}
                quizData={quizData}
                showQuizModal={showQuizModal}
                onCloseQuizModal={() => setShowQuizModal(false)}
                onSaveQuiz={async (editedQuiz) => {
                    setQuizData(editedQuiz);
                }}
                toast={toast}
                showLogoutConfirm={showLogoutConfirm}
                onCloseLogoutConfirm={() => setShowLogoutConfirm(false)}
                onConfirmLogout={() => {
                    setShowLogoutConfirm(false);
                    onLogout();
                }}
                isLoading={isLoading}
                lessonProgressMsg={lessonProgressMsg}
                showAssistantDrawer={showAssistantDrawer}
                onCloseAssistantDrawer={() => setShowAssistantDrawer(false)}
                selectedSubject={selectedSubject}
                gradeLevel={gradeLevel}
                userRole={userRole}
            />
        </div>

    );
};

export default ProductionDashboard;
